import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Heart, ArrowRight } from "lucide-react";

export default function OnboardMagicLink() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh: refreshAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/onboard/${token}`)
      .then(({ data }) => {
        setLinkData(data);
        setName(data.user?.name || "");
        setLoading(false);
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "Invite link is invalid or expired.");
        setLoading(false);
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/onboard/set-password", { token, password, name });
      localStorage.setItem("cc_token", data.token);
      await refreshAuth();
      toast.success("Account activated. Welcome, Founding Partner!");
      nav("/ad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Activation failed. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg text-white flex items-center justify-center">
        <div className="heading text-3xl">LOADING…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-appbg text-white flex items-center justify-center px-6">
        <div className="max-w-lg text-center" data-testid="onboard-error">
          <div className="text-mono text-blaze text-xs tracking-widest mb-3">ONBOARD ERROR</div>
          <h1 className="heading text-3xl mb-4">{error}</h1>
          <p className="text-neutral-400 text-sm">
            If you believe this is wrong, reply to the invite email and we'll sort it out.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg text-white">
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-16" data-testid="onboard-page">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blaze/10 border border-blaze text-blaze text-[11px] tracking-widest heading mb-6">
          <Heart size={12} strokeWidth={2} fill="currentColor" />
          FOUNDING PARTNER · COMPLIMENTARY ACCESS
        </div>

        <h1 className="heading text-4xl md:text-5xl leading-tight" data-testid="onboard-headline">
          Welcome to <span className="text-blaze">CoachConnect AI</span>.
          <br />Let's activate your account.
        </h1>

        <p className="text-neutral-300 text-base md:text-lg mt-6 leading-relaxed">
          You're signing in as <span className="text-white font-medium" data-testid="onboard-email">{linkData?.user?.email}</span>.
          Set a password to access your dashboard. All paid features are unlocked — no credit card needed.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6">
          <Field label="Your Name (or program name)">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="onboard-name-input"
              className="w-full bg-neutral-900 border border-neutral-800 px-4 py-3 text-white focus:border-blaze outline-none transition-colors"
              placeholder="e.g., Cape Henlopen High School AD"
            />
          </Field>
          <Field label="Set a password (8+ characters)">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="onboard-password-input"
              className="w-full bg-neutral-900 border border-neutral-800 px-4 py-3 text-white focus:border-blaze outline-none transition-colors"
              placeholder="••••••••"
            />
          </Field>
          <Field label="Confirm password">
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              data-testid="onboard-confirm-input"
              className="w-full bg-neutral-900 border border-neutral-800 px-4 py-3 text-white focus:border-blaze outline-none transition-colors"
              placeholder="••••••••"
            />
          </Field>
          <button
            type="submit"
            disabled={submitting}
            data-testid="onboard-submit-btn"
            className="w-full bg-blaze text-white heading text-lg tracking-wider py-4 hover:glow-blaze transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? "ACTIVATING…" : "ACTIVATE MY ACCOUNT"} <ArrowRight size={18} />
          </button>
        </form>

        <div className="mt-10 text-neutral-500 text-xs leading-relaxed border-t border-neutral-800 pt-6">
          By activating, you agree to share feedback at 30, 90, and 180 days. We'll
          use what you share to keep improving — and (no pressure) write a short
          case study if we help you fill the role.
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs tracking-widest text-neutral-400 mb-2 heading">{label.toUpperCase()}</div>
      {children}
    </label>
  );
}
